import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoCapricornio = () => {
  const articleSchema = createArticleSchema(
    "Capricornio: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Capricornio. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 22 de diciembre y el 19 de enero.",
    `${window.location.origin}/horoscopo-capricornio`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Capricornio",
        "item": `${window.location.origin}/horoscopo-capricornio`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Capricornio" }
  ];

  return (
    <>
      <Helmet>
        <title>Capricornio: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Capricornio. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 22 de diciembre y el 19 de enero." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-capricornio`} />
        <meta property="og:title" content="Capricornio: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Capricornio. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-capricornio`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♑</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Capricornio
              </h1>
              <p className="text-xl text-muted-foreground">22 de Diciembre - 19 de Enero</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Capricornio: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="capricornio" signName="Capricornio" />
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Capricornio: El Maestro de la Meta</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Capricornio</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">🌍 Elemento: Tierra</h4>
                    <p className="text-muted-foreground">Enfoque en la estructura, la ambición y el estatus. Capricornio construye imperios duraderos.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🏔️ Modalidad: Cardinal</h4>
                    <p className="text-muted-foreground">Define la iniciación de proyectos a largo plazo y la disciplina. Líder natural en el mundo material.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">♄ Planeta Regente: Saturno</h4>
                    <p className="text-muted-foreground">El Señor del Tiempo, el Límite y la Responsabilidad. Maestro de la paciencia y la madurez.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Capricornio: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Disciplina:</strong> Autocontrol excepcional</li>
                      <li>• <strong>Responsabilidad:</strong> Cumple siempre sus compromisos</li>
                      <li>• <strong>Ambición:</strong> Visión clara de sus metas</li>
                      <li>• <strong>Paciencia:</strong> Dispuesto a esperar el éxito</li>
                      <li>• <strong>Pragmatismo:</strong> Enfoque realista y efectivo</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Pesimismo:</strong> Tendencia a ver lo negativo</li>
                      <li>• <strong>Rigidez:</strong> Dificultad para adaptarse</li>
                      <li>• <strong>Frialdad Emocional:</strong> Dificultad para expresar sentimientos</li>
                      <li>• <strong>Materialismo:</strong> Énfasis excesivo en el estatus</li>
                      <li>• <strong>Workaholic:</strong> Sacrifica vida personal por trabajo</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Utilizo"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Capricornio en el Amor: Seriedad, Compromiso y Lealtad a Largo Plazo</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Capricornio no juega con el amor. Busca relaciones serias con potencial de futuro desde el principio. Su amor es lento para desarrollarse pero inquebrantable una vez establecido. Valora la estabilidad, el respeto mutuo y la construcción de un futuro sólido juntos.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita una pareja que comparta sus valores de trabajo duro, responsabilidad y ambición. Puede parecer frío al principio, pero bajo su exterior reservado late un corazón leal y protector. Su desafío es aprender a expresar sus emociones y no dejar que el trabajo consuma toda su energía.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Capricornio en el Trabajo y Finanzas: Éxito Profesional y Acumulación de Patrimonio</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Capricornio destaca en administración, gerencia, política, arquitectura, ingeniería, finanzas corporativas y cualquier campo que requiera planificación estratégica a largo plazo. Su capacidad para escalar jerarquías y asumir responsabilidades es legendaria.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Capricornio es el constructor de imperios. Ahorra metódicamente, invierte con sabiduría y construye patrimonio generacional. Valora la seguridad financiera como base para todo lo demás. Excelente para gestión de recursos, planificación de retiro y construcción de legados duraderos.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Capricornio con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♉ Tauro y ♍ Virgo</h4>
                    <p className="text-muted-foreground">
                      Los signos de tierra comparten valores de trabajo duro y resultados tangibles. Con Tauro hay estabilidad sensorial y disfrute de los logros. Con Virgo, una alianza de excelencia mutua y apoyo práctico constante.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♏ Escorpio</h4>
                    <p className="text-muted-foreground">
                      Escorpio ofrece la intensidad emocional que complementa la estructura de Capricornio. Ambos son signos de poder, lealtad y transformación. Una unión formidable basada en respeto mutuo y objetivos compartidos.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Capricornio</h3>
                <p className="text-muted-foreground mb-4">
                  El disfrute y la vulnerabilidad son los grandes desafíos de Capricornio. Cáncer, su signo opuesto, le enseña sobre la importancia de las emociones, el hogar y el cuidado personal más allá del éxito profesional.
                </p>
                <p className="text-muted-foreground">
                  Los signos de fuego (Aries, Leo, Sagitario) desafían su seriedad con espontaneidad y alegría. Los signos de aire (Géminis, Libra, Acuario) le enseñan sobre flexibilidad mental y la importancia de las relaciones sobre los logros materiales.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoCapricornio;
